<template>
  <section class="admin-page">
    <div class="cover"></div>

    <div class="page-title">
      <h2>{{ isEdit ? 'Izmeni' : 'Dodaj novu' }} stanicu</h2>
      <router-link :to="{ name: 'Pages' }">
        <el-button type="info" icon="el-icon-back">Nazad na sve stranice</el-button>
      </router-link>
    </div>

    <el-card class="box-card">
      <div slot="header" class="header">
        <span>Podešavanje stranice za jezik:</span>
        <el-button-group style="float: right; padding: 3px 0">
          <el-button
            v-for="(lang, key) in langs"
            :key="key"
            :type="selectedLang === key ? 'primary' : 'default'"
            size="mini"
            round
            @click="selectedLang = key">
            {{ lang }}
          </el-button>
        </el-button-group>
      </div>
      <div>
        <h2>Info stranice - SEO</h2>

        <label>Naslov stranice *</label>
        <el-input v-model="form.data[selectedLang].title" placeholder="Naslov stranice"></el-input>

        <label>Meta text *</label>
        <el-input
         v-model="form.data[selectedLang].meta"
          type="textarea"
          placeholder="Meta text"
          maxlength="160"
          show-word-limit />

        <label>URL *</label>
        <el-input v-model="url[selectedLang]" placeholder="URL (primer: bianco-cimbro)" :disabled="edit && !edit.deletable" @input="$v.url[selectedLang].$touch()">
          <template slot="prepend">{{ urltext }}
          </template>
        </el-input>
        <span v-if="$v.url[selectedLang].$invalid && $v.url[selectedLang].$dirty" class="error">Url može da sadrži samo mala slova, brojeve i crtice (bez razmaka)</span>
      </div>
      <div class="mt-30">
        <h2>Sadržaj</h2>

        <label>Hero image text *</label>
        <el-input
          v-model="form.data[selectedLang].h1"
          type="textarea"
          placeholder="Hero text"
          maxlength="160"
          show-word-limit />

        <label>Sadržaj *</label>
        <editor
          v-model="form.data[selectedLang].content"
          apiKey="nj9qdh9hz9irpq5na7ir81n1seqz341wql2t590jwfojfns2"
          :init="options">
        </editor>
      </div>

      <template v-if="availablePages.length">
        <label>Stranica je pod stranicom</label>
        <el-select
          v-model="form.parent"
          placeholder="Izaberi stranicu"
          no-match-text="Prazno."
          clearable>
          <el-option
            v-for="item in availablePages"
            :key="item.id"
            :label="item.text"
            :value="item.id" />
        </el-select>
      </template>

    </el-card>

    <el-card class="mt-30">
      <h2>FAQ</h2>

      <div v-if="form.data[selectedLang].faq && form.data[selectedLang].faq.length">
        <div v-for="(item, index) in form.data[selectedLang].faq" :key="index">
          <label>Pitanje #{{ index + 1 }} <el-button type="text" @click="removeQuestion(index)">Obriši</el-button></label>
          <el-input v-model="form.data[selectedLang].faq[index].title" placeholder="Pitanje" />

          <el-input
            v-model="form.data[selectedLang].faq[index].text"
            type="textarea"
            placeholder="Odgovor"
            class="mt-10" />
        </div>
      </div>

      <div v-else class="empty-placeholder">
        Nema dodatih pitanja
      </div>

      <div class="mt-10">
        <el-button type="info" icon="el-icon-plus" size="small" round @click="addQuestion()">Dodaj pitanje</el-button>
      </div>
    </el-card>

    <el-card class="mt-30">
      <h2>Galerija</h2>

      <div class="gallery mt-10">
        <div
          v-for="(img, index) in form.images"
          :key="index"
          class="gallery-item">
          <i class="close el-icon-error" @click="removeGalleryImage(index)" />
          <div class="image">
            <img :src="img.image">
          </div>
          <el-input
            v-model="form.images[index].description"
            placeholder="Opis"
            class="mt-10" />
        </div>

        <div v-if="!form.images.length" class="empty-placeholder">
          Nema dodatih slika u galeriji
        </div>
      </div>

      <div class="mt-10">
        <input v-show="false" ref="gallery" type="file" accept="image/*" multiple @change="selectGalleryImages">
        <el-button type="info" icon="el-icon-picture" size="small" round @click="$refs.gallery.click()">Dodaj slike u galeriju</el-button>
      </div>
    </el-card>

    <el-card class="hero-image mt-30" :body-style="{ padding: '0px' }">
      <div class="image">
        <i v-if="imageExists" class="close el-icon-error" @click="removeImage()" />
        <img :src="showImage">
      </div>
      <div class="content">
        <input v-show="false" ref="image" type="file" accept="image/*" @change="selectImage">
        <span>Preporucena velicina slike: 1920x460 pixela</span>
        <el-button type="info" icon="el-icon-picture" size="small" round @click="$refs.image.click()">{{ imageExists ? 'Promeni' : 'Dodaj' }} hero image</el-button>
      </div>
    </el-card>

    <div class="mt-30">
      <el-button :disabled="$v.$invalid" type="primary" icon="el-icon-check" @click="save()">{{ isEdit ? 'Izmeni' : 'Dodaj' }} stranu</el-button>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import Editor from '@tinymce/tinymce-vue'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { Button, Card, Select, ButtonGroup, Input, Option, Loading, Message } from 'element-ui'

import emptyImg from '@/assets/images/hero-empty.webp'

const slug = (value) => /^[a-z0-9](-?[a-z0-9])*$/.test(value)

export default {
  name: 'ManagePage',

  mixins: [validationMixin],

  beforeRouteEnter (to, from, next) {
    const promises = [axios.get('/pages')]
    if (to.name === 'EditPage') promises.push(axios.get(`/page/${to.params.id}`))
    Promise.all(promises).then(response => {
      next(vm => {
        vm.pages = response[0].data.data
        if (response[1]) vm.edit = response[1].data.data
        vm.init()
      })
    })
  },

  components: {
    Editor,
    ElCard: Card,
    ElInput: Input,
    ElButton: Button,
    ElOption: Option,
    ElSelect: Select,
    ElButtonGroup: ButtonGroup
  },

  data () {
    return {
      selectedLang: 'rs',
      previewImage: null,
      url: {
        en: null,
        rs: null
      },
      form: {
        image: null,
        parent: null,
        data: {
          en: {
            title: null,
            meta: null,
            h1: null,
            content: '',
            faq: []
          },
          rs: {
            title: null,
            meta: null,
            h1: null,
            content: '',
            faq: []
          }
        },
        images: []
      },
      images: [],
      pages: [],
      langs: {
        rs: 'Srpski',
        en: 'Engleski'
      },
      options: {
        height: 400,
        menubar: false,
        plugins: [
          'advlist autolink lists link image charmap',
          'searchreplace visualblocks code fullscreen',
          'print preview anchor insertdatetime media',
          'paste code help wordcount table pageembed',
          'image'
        ],
        toolbar: `undo redo | formatselect fontsizeselect | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | image link media pageembed`
      },
      edit: null,
      loadingImage: false,
      emptyImg
    }
  },

  computed: {
    urltext () {
      let text = 'https://mondial90.rs/'
      const addon = {
        rs: 'proizvodi/',
        en: 'products/'
      }
      if (!this.edit || this.edit.deletable) {
        text += addon[this.selectedLang]
      }
      return text
    },

    isEdit () {
      return this.$route.name === 'EditPage'
    },

    availablePages () {
      return this.pages.filter(page => page.deletable && !page.parent && (!this.isEdit || (this.isEdit && page.id !== this.edit.id))).map(page => {
        return {
          id: page.id,
          text: page.data.rs.title
        }
      })
    },

    showImage () {
      return this.previewImage || this.emptyImg
    },

    imageExists () {
      return !!(this.previewImage || this.form.cover)
    }
  },

  methods: {
    addQuestion () {
      if (!this.form.data[this.selectedLang].faq) {
        this.form.data[this.selectedLang].faq = []
      }

      this.form.data[this.selectedLang].faq.push({
        title: '',
        text: ''
      })
    },

    removeQuestion (index) {
      this.form.data[this.selectedLang].faq.splice(index, 1)
    },

    async save () {
      const loading = Loading.service()
      try {
        const payload = new FormData()
        payload.append('url', this.url.rs)
        payload.append('urlEN', this.url.en)
        Object.keys(this.form).forEach(key => {
          if (!['images'].includes(key)) {
            payload.append(key, key === 'data' ? JSON.stringify(this.form[key]) : this.form[key])
          }
        })

        if (this.form.images.length) {
          this.form.images.forEach((img, index) => {
            // payload.append(`images[image][]`, img.image || null)
            if (img.file) {
              payload.append(`images[]`, img.file || null)
              payload.append(`descriptions[]`, img.description || '')
            } else {
              payload.append(`oldimages[${index}][image]`, img.path)
              payload.append(`oldimages[${index}][description]`, img.description || '')
            }
          })
        }

        const route = this.isEdit ? `/page/${this.edit.id}` : '/page'
        const { data } = await axios.post(route, payload)
        this.edit = data.data
        this.init()
        Message.success('Stranica uspešno sačuvana.')
        if (!this.isEdit) {
          this.$router.push({ name: 'Pages' })
        }
      } catch {
      } finally {
        loading.close()
      }
    },

    init () {
      if (this.edit) {
        this.url.rs = this.edit.url
        this.url.en = this.edit.urlEN
        this.previewImage = this.edit.image ? this.$path + this.edit.image : null
        this.form = {
          image: this.edit.image,
          parent: this.edit.parent,
          data: {
            en: {
              title: this.edit.data.en.title,
              meta: this.edit.data.en.meta,
              h1: this.edit.data.en.h1,
              content: this.edit.data.en.content,
              faq: this.edit.data.en.faq || []
            },
            rs: {
              title: this.edit.data.rs.title,
              meta: this.edit.data.rs.meta,
              h1: this.edit.data.rs.h1,
              content: this.edit.data.rs.content,
              faq: this.edit.data.rs.faq || []
            }
          },
          images: (this.edit.images || []).map(img => ({
            path: img.image,
            image: this.$path + img.image,
            description: img.description || ''
          }))
        }
      }
    },

    removeGalleryImage (index) {
      this.form.images.splice(index, 1)
    },

    removeImage () {
      this.form.image = null
      this.previewImage = null
      this.$refs.image.value = null
    },

    selectGalleryImages (event) {
      const files = event.target.files
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const reader = new FileReader()
          reader.onload = (e) => {
            this.form.images.push({
              file,
              image: e.target.result,
              description: null
            })
          }
          reader.readAsDataURL(file)
        }
      }
      event.target.value = ''
    },

    selectImage () {
      this.form.image = this.$refs.image.files[0]
      if (this.form.image) {
        this.loadingImage = true
        const reader = new FileReader()
        reader.onload = (e) => {
          this.previewImage = e.target.result
          this.loadingImage = false
        }
        reader.readAsDataURL(this.form.image)
      }
    }
  },

  validations: {
    url: {
      rs: { required, slug },
      en: { required, slug }
    },
    form: {
      image: { required },
      data: {
        en: {
          title: { required },
          meta: { required },
          h1: { required },
          content: { required }
        },
        rs: {
          title: { required },
          meta: { required },
          h1: { required },
          content: { required }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
h2 {
  font-size: 19px;
}
label {
  display: block;
  margin-top: 20px;
  margin-bottom: 10px;
}
.error {
  display: block;
  margin-top: 5px;
  color: rgb(212, 63, 63);
  font-size: 14px;
}
.box-card {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.hero-image {
  width: 100%;
  .image {
    width: 100%;
    height: 460px;
    position: relative;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .close {
      position: absolute;
      top: 15px;
      right: 15px;
      font-size: 25px;
      color: #fff;
      cursor: pointer;
      background-color: black;
      border-radius: 50%;
    }
  }
  .content {
    text-align: center;
    padding: 20px;
    span {
      display: block;
      color: #999;
      font-size: 13px;
      margin-bottom: 10px;
    }
  }
}

.gallery {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  .gallery-item {
    width: 300px;
    border: 1px solid rgba(0, 0, 0, .1);
    margin-right: 10px;
    margin-bottom: 10px;
    border-radius: 3px;
    padding: 5px;
    position: relative;
    .image {
      height: 300px;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .close {
        position: absolute;
        top: 15px;
        right: 15px;
        font-size: 25px;
        color: #fff;
        cursor: pointer;
        background-color: black;
        border-radius: 50%;
      }
  }
}

.empty-placeholder {
  display: block;
  color: #999;
  font-size: 13px;
  margin-top: 10px;
}
</style>
